// OptionSection.js
import React, { useState } from 'react';
import {
    Box,
    Typography,
    TextField,
    FormControlLabel,
    Switch,
    IconButton,
    ListItemButton,
    Collapse,
} from '@mui/material';
import { FaTrash, FaChevronDown } from 'react-icons/fa';
import { ItemSelectionSection } from './ItemSelectionSection';

const OptionSection = ({
    option,
    typeInfo,
    menuItems,
    categories,
    onItemSelection,
    onMultipleItemSelection,
    onExtraPriceChange,
    onRemoveOption,
    onOptionChange
}) => {
    const [isExpanded, setIsExpanded] = useState(true);

    // Créer un résumé compact des items sélectionnés
    const selectedItemsCount = option.items.length;
    const itemsSummary = option.items
        .map(item => menuItems.find(mi => mi.id === item.id)?.name)
        .filter(Boolean)
        .join(", ");

    const truncatedSummary = itemsSummary.length > 60
        ? `${itemsSummary.slice(0, 60)}...`
        : itemsSummary;

    const handleQuantityChange = (e) => {
        const value = parseInt(e.target.value) || 1;
        onOptionChange(option.id, 'quantity', Math.max(1, value));
    };

    const handleRequiredChange = (e) => {
        onOptionChange(option.id, 'required', e.target.checked);
    };

    return (
        <Box sx={{
            mb: 2,
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 1,
            overflow: 'hidden'
        }}>
            {/* Header */}
            <ListItemButton
                onClick={() => setIsExpanded(!isExpanded)}
                sx={{
                    p: 2,
                    bgcolor: 'background.paper',
                    '&:hover': { bgcolor: 'action.hover' }
                }}
            >
                <Box sx={{
                    display: 'flex',
                    width: '100%',
                    justifyContent: 'space-between',
                    alignItems: 'center'
                }}>
                    <Box sx={{ flexGrow: 1 }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                            {typeInfo.name}
                        </Typography>
                        {!isExpanded && selectedItemsCount > 0 && (
                            <Typography
                                variant="body2"
                                color="text.secondary"
                                sx={{
                                    mt: 0.5,
                                    display: '-webkit-box',
                                    WebkitLineClamp: 1,
                                    WebkitBoxOrient: 'vertical',
                                    overflow: 'hidden'
                                }}
                            >
                                {selectedItemsCount} item{selectedItemsCount > 1 ? 's' : ''} : {truncatedSummary}
                            </Typography>
                        )}
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <IconButton
                            size="small"
                            onClick={(e) => {
                                e.stopPropagation();
                                onRemoveOption(option.id);
                            }}
                            sx={{ color: 'error.main' }}
                        >
                            <FaTrash />
                        </IconButton>
                        <FaChevronDown
                            style={{
                                transform: isExpanded ? 'rotate(180deg)' : 'none',
                                transition: 'transform 0.3s'
                            }}
                        />
                    </Box>
                </Box>
            </ListItemButton>

            {/* Content */}
            <Collapse in={isExpanded}>
                <Box sx={{ p: 2 }}>
                    {/* Controls */}
                    <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                        <TextField
                            type="number"
                            label="Quantité"
                            value={option.quantity}
                            onChange={handleQuantityChange}
                            size="small"
                            sx={{ width: 100 }}
                            InputProps={{ inputProps: { min: 1 } }}
                        />
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={option.required}
                                    onChange={handleRequiredChange}
                                    size="small"
                                />
                            }
                            label="Obligatoire"
                        />
                    </Box>

                    {/* Selected Items and Item Selection */}
                    <Box sx={{ mt: 2 }}>
                        {/* Item Selection */}
                        <ItemSelectionSection
                            optionId={option.id}
                            selectedItems={option.items}
                            menuItems={menuItems}
                            categories={categories}
                            onItemSelect={(itemId) => onItemSelection(option.id, itemId)}
                            onMultipleSelect={onMultipleItemSelection}
                            onExtraPriceChange={onExtraPriceChange}
                        />
                    </Box>
                </Box>
            </Collapse>
        </Box>
    );
};

export default OptionSection;